import { FormEvent, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import '../Roles/roles.css';
import './premium.css';
import { EmptyState } from '../../../components/common/EmptyState/EmptyState';
import { useStationsListQuery } from '../../../hooks/useDashboardQueries';
import {
  useCreatePremiumMutation,
  useDeletePremiumMutation,
  usePremiumsQuery,
  useUpdatePremiumPriceMutation,
} from '../../../hooks/usePremiumQueries';
import { PremiumGrantV2 } from '../../../types/premium';
import { parseApiError } from '../../../utils/parseApiError';
import { TableActionButton } from '../components/cells';

const ALL_STATIONS = '';

const errorText = (error: unknown) => (error ? parseApiError(error).message : undefined);

const parsePrice = (value: string): number | null => {
  const n = Number(value.replace(',', '.'));
  return value.trim() !== '' && Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
};

const userLabel = (g: PremiumGrantV2) =>
  [g.user?.firstName, g.user?.lastName].filter(Boolean).join(' ') ||
  g.user?.phone ||
  g.user?.email ||
  '—';

interface PremiumGrantsProps {
  /** undefined = the caller's own organization; set = the platform-admin path. */
  organizationId?: string;
}

/**
 * Premium prices an organization grants: one user pays a set price per kWh
 * at every station of the organization, or at one of them. A station
 * price beats an all-stations price.
 */
export const PremiumGrants = ({ organizationId }: PremiumGrantsProps) => {
  const { t } = useTranslation();
  const grants = usePremiumsQuery(organizationId);
  const stations = useStationsListQuery({
    search: '',
    organizationIds: organizationId,
    enabled: !grants.isError,
  });
  const create = useCreatePremiumMutation(organizationId);
  const updatePrice = useUpdatePremiumPriceMutation(organizationId);
  const remove = useDeletePremiumMutation(organizationId);

  const [phone, setPhone] = useState('');
  const [stationId, setStationId] = useState(ALL_STATIONS);
  const [price, setPrice] = useState('');
  const [editing, setEditing] = useState<{ id: string; value: string } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PremiumGrantV2 | null>(null);

  if (grants.isLoading) return null;
  if (grants.isError) {
    return (
      <EmptyState
        icon="workspace_premium"
        message={errorText(grants.error) ?? t('dashboard.premium.loadFailed')}
      />
    );
  }

  const list = grants.data ?? [];
  const parsedPrice = parsePrice(price);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!phone.trim() || parsedPrice === null) return;
    try {
      await create.mutateAsync({
        phone: phone.trim(),
        stationId: stationId || null,
        pricePerKwh: parsedPrice,
      });
      setPhone('');
      setPrice('');
    } catch {
      // Shown from create.error.
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const value = parsePrice(editing.value);
    if (value === null) return;
    try {
      await updatePrice.mutateAsync({ id: editing.id, pricePerKwh: value });
      setEditing(null);
    } catch {
      // Shown from updatePrice.error.
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync(pendingDelete.id);
      setPendingDelete(null);
    } catch {
      // Shown from remove.error.
    }
  };

  return (
    <div className="premium-page">
      <section className="premium-panel" aria-labelledby="premium-title">
        <header className="premium-panel-header">
          <h3 id="premium-title" className="premium-title">
            {t('dashboard.premium.title', { count: list.length })}
          </h3>
          <p className="premium-hint">{t('dashboard.premium.hint')}</p>
        </header>

        <form className="premium-add" onSubmit={(e) => void submit(e)}>
          <input
            type="tel"
            className="filter-search-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t('dashboard.team.members.phonePlaceholder')}
            aria-label={t('dashboard.team.members.phoneLabel')}
            disabled={create.isPending}
          />
          <select
            className="filter-select-input"
            value={stationId}
            onChange={(e) => setStationId(e.target.value)}
            aria-label={t('dashboard.premium.stationLabel')}
            disabled={create.isPending}
          >
            <option value={ALL_STATIONS}>{t('dashboard.premium.allStations')}</option>
            {(stations.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.address ? `${s.idTag} · ${s.address}` : s.idTag}
              </option>
            ))}
          </select>
          <input
            type="text"
            inputMode="decimal"
            className="filter-search-input"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder={t('dashboard.premium.pricePlaceholder')}
            aria-label={t('dashboard.premium.priceLabel')}
            disabled={create.isPending}
          />
          <button
            type="submit"
            className="primary-action-btn"
            disabled={!phone.trim() || parsedPrice === null || create.isPending}
          >
            {t('dashboard.premium.add')}
          </button>
        </form>
        {(create.error || updatePrice.error) && (
          <div className="premium-errors">
            {[create.error, updatePrice.error].filter(Boolean).map((err, i) => (
              <p key={i} className="role-form-error" role="alert">
                {errorText(err)}
              </p>
            ))}
          </div>
        )}

        {list.length === 0 ? (
          <p className="premium-empty">{t('dashboard.premium.empty')}</p>
        ) : (
          <ul className="premium-list">
            {list.map((g) => (
              <li key={g.id} className="premium-row">
                <div className="premium-row-main">
                  <div className="premium-row-title">{userLabel(g)}</div>
                  <div className="premium-row-meta">
                    {[
                      // The phone is already the title when the user has no name.
                      g.user?.phone !== userLabel(g) && g.user?.phone,
                      g.station
                        ? [g.station.idTag, g.station.address].filter(Boolean).join(' · ')
                        : t('dashboard.premium.allStations'),
                    ]
                      .filter(Boolean)
                      .join(' — ')}
                  </div>
                </div>
                {editing?.id === g.id ? (
                  <div className="premium-row-actions">
                    <input
                      type="text"
                      inputMode="decimal"
                      className="filter-search-input premium-price-input"
                      value={editing.value}
                      onChange={(e) => setEditing({ id: g.id, value: e.target.value })}
                      aria-label={t('dashboard.premium.priceLabel')}
                      autoFocus
                    />
                    <TableActionButton
                      icon="check"
                      onClick={() => void saveEdit()}
                      disabled={parsePrice(editing.value) === null || updatePrice.isPending}
                      ariaLabel={t('dashboard.premium.savePrice')}
                    />
                    <TableActionButton
                      icon="close"
                      onClick={() => setEditing(null)}
                      ariaLabel={t('dashboard.organizations.cancel')}
                    />
                  </div>
                ) : (
                  <div className="premium-row-actions">
                    <span className="premium-price">
                      {t('dashboard.premium.pricePerKwh', { price: g.pricePerKwh.toFixed(2) })}
                    </span>
                    <TableActionButton
                      icon="edit"
                      onClick={() => {
                        updatePrice.reset();
                        setEditing({ id: g.id, value: String(g.pricePerKwh) });
                      }}
                      ariaLabel={t('dashboard.premium.editAria', { name: userLabel(g) })}
                    />
                    <TableActionButton
                      icon="delete"
                      onClick={() => {
                        remove.reset();
                        setPendingDelete(g);
                      }}
                      ariaLabel={t('dashboard.premium.deleteAria', { name: userLabel(g) })}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('dashboard.premium.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {pendingDelete && t('dashboard.premium.deleteConfirm', { name: userLabel(pendingDelete) })}
          </Typography>
          {remove.error && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }} role="alert">
              {errorText(remove.error)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <button type="button" className="secondary-action-btn" onClick={() => setPendingDelete(null)}>
            {t('dashboard.organizations.cancel')}
          </button>
          <button
            type="button"
            className="danger-action-btn"
            onClick={() => void confirmDelete()}
            disabled={remove.isPending}
          >
            {t('dashboard.common.delete')}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PremiumGrants;
